import { clearAllMutations } from '@lidea/shared/data-access/core';
import {
  clearPendingCropPictures,
} from '@lidea/shared/data-access/analysis';
import { QueryClient } from '@tanstack/react-query';
import { clear, createStore, del } from 'idb-keyval';

import { clearOfflineScopeSession } from './offline-scope-session';
import { isSyncRunnerActive, pauseSyncJob } from './sync-job-runner';
import { clearSyncJob } from './sync-job-store';
import { SYNC_JOB_STORAGE_KEY } from './types';

/** TanStack global persister key (see apps/mobile create-idb-persister). */
export const TANSTACK_QUERY_IDB_KEY = 'tanstackQuery';

/** Per-query experimental persister IDB store names. */
export const OFFLINE_QUERY_PERSISTER_STORES = [
  'plots',
  'plotHeatmaps',
  'imageryIndexes',
] as const;

const OFFLINE_SYNC_DONE_PREFIX = 'offline_sync_done:';
const LEGACY_INITIAL_CACHE_KEY = 'pf.initial_cache_setted';

/** Legacy: forces bootstrap re-run (logout only). */
export const OFFLINE_BOOTSTRAP_RESET_KEY = 'lidea.offline_force_bootstrap';

/** When set, SetupCache will not start auto-sync until explicit resync or next login. */
export const SKIP_AUTO_SYNC_SESSION_KEY = 'lidea.skip_auto_sync';

export const OFFLINE_CACHE_CLEARED_EVENT = 'lidea-offline-cache-cleared';

export function setSkipAutoSyncSession(): void {
  window.sessionStorage.setItem(SKIP_AUTO_SYNC_SESSION_KEY, '1');
}

export function shouldSkipAutoSyncSession(): boolean {
  return window.sessionStorage.getItem(SKIP_AUTO_SYNC_SESSION_KEY) === '1';
}

export function clearSkipAutoSyncSession(): void {
  window.sessionStorage.removeItem(SKIP_AUTO_SYNC_SESSION_KEY);
}

export function notifyOfflineCacheCleared(): void {
  window.dispatchEvent(new CustomEvent(OFFLINE_CACHE_CLEARED_EVENT));
}

export function clearOfflineSyncDoneFlags(): void {
  const keysToRemove: string[] = [];
  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (
      key &&
      (key.startsWith(OFFLINE_SYNC_DONE_PREFIX) ||
        key === LEGACY_INITIAL_CACHE_KEY)
    ) {
      keysToRemove.push(key);
    }
  }
  keysToRemove.forEach((key) => window.localStorage.removeItem(key));
}

async function clearExperimentalPersisterStores(): Promise<void> {
  await Promise.all(
    OFFLINE_QUERY_PERSISTER_STORES.map((storeName) =>
      clear(createStore(storeName, storeName))
    )
  );
}

async function clearPwaRuntimeCaches(): Promise<void> {
  if (typeof caches === 'undefined') {
    return;
  }
  const names = await caches.keys();
  await Promise.all(
    names
      .filter(
        (name) =>
          name.startsWith('lidea-') ||
          name.startsWith('mapbox-') ||
          name.includes('workbox')
      )
      .map((name) => caches.delete(name))
  );
}

export type ClearOfflineCacheReason = 'user' | 'logout';

export type ClearOfflineCacheOptions = {
  queryClient: QueryClient;
  /** Also wipe in-memory React Query cache (recommended on logout). */
  clearQueryClient?: boolean;
  /** Clear persisted mutations queue. */
  clearMutations?: boolean;
  /**
   * user: manual clear from control center — keep CDS scope, no auto-sync after.
   * logout: full reset including scope.
   */
  reason?: ClearOfflineCacheReason;
};

/**
 * Full offline reset: sync job metadata, IDB caches, scope session, done flags.
 */
export async function clearOfflineCache(
  options: ClearOfflineCacheOptions
): Promise<void> {
  const {
    queryClient,
    clearQueryClient = true,
    clearMutations = true,
    reason = 'user',
  } = options;

  if (isSyncRunnerActive()) {
    await pauseSyncJob();
  }

  await clearSyncJob();
  if (reason === 'logout') {
    clearOfflineScopeSession();
  }
  clearOfflineSyncDoneFlags();
  setSkipAutoSyncSession();

  await Promise.all([
    del(TANSTACK_QUERY_IDB_KEY),
    del(SYNC_JOB_STORAGE_KEY),
    clearExperimentalPersisterStores(),
    clearPwaRuntimeCaches(),
    clearPendingCropPictures(),
  ]);

  if (clearMutations) {
    clearAllMutations(queryClient);
  }

  if (clearQueryClient) {
    queryClient.clear();
  }

  if (reason === 'logout') {
    window.sessionStorage.setItem(OFFLINE_BOOTSTRAP_RESET_KEY, '1');
  }
  notifyOfflineCacheCleared();
}
